import React from 'react';
import { Button } from 'antd';
import { Link } from 'react-router-dom';
import { isNil } from 'lodash';

import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import { ValidationFailure } from 'api/backofficeApiClient';
import { getClientFieldName } from 'utils/clientUtils';
import { AdminRequestsSections } from 'features/admin/request/RequestsPage/enums';
import { ValidationDetailsModel } from 'api/adminApiClient';
import { getAssignmentFieldName } from 'utils/bulkImportUtils';

import styles from './BulkImportResultModal.module.less';

export interface BulkImportResultModel {
  validationFailures?: ValidationFailure[] | undefined;
  success?: boolean;
  errors?: ValidationDetailsModel[];
}

export enum BulkImportMode {
  Customer = 'customer',
  Assignment = 'assignment',
}

type Props = {
  onClose: () => void;
  results: BulkImportResultModel[];
  mode: BulkImportMode;
};

export default function BulkImportResultModal(props: Props) {
  const { onClose, results, mode } = props;

  const isAssignmentMode = mode === BulkImportMode.Assignment;

  const successfulCreationOfData = results.filter((result) => result.success);

  const handleCloseModal = () => {
    onClose();
  };

  const isSuccess = successfulCreationOfData.length === results.length;

  const isFailed = successfulCreationOfData.length === 0;

  const getCorrectTitle = () => {
    if (isFailed) return 'File Upload Failed';

    if (isSuccess) return 'File Upload Completed';

    return 'File Upload Completed (with Errors)';
  };

  const getCorrectDescription = () => {
    if (isFailed)
      return `The import process is finished. Unfortunately, no ${
        isAssignmentMode ? 'assignment' : 'customer'
      } records were created. Please check the mandatory fields and validation rules on your file and try again.`;

    if (isSuccess)
      return `The import process is finished. ${
        successfulCreationOfData.length
      } out of ${results.length} ${
        isAssignmentMode ? 'assignment ' : 'customer '
      } records were created successfully.`;

    return renderErrorDetails();
  };

  const renderErrorDetails = () => {
    const arrayOfErrors = results
      .map((result, index) => {
        if (result.success) return undefined;
        const fieldErrors = (result.validationFailures || [])
          .map((validationFailure) =>
            !isNil(validationFailure.propertyName)
              ? isAssignmentMode
                ? getAssignmentFieldName(validationFailure.propertyName)
                : getClientFieldName(validationFailure.propertyName)
              : false,
          )
          .filter(Boolean);

        const limitErrors = (result.errors || [])
          .map((error) => error.errorMessage)
          .filter(Boolean);

        const errors = fieldErrors.concat(limitErrors);

        return {
          line: isAssignmentMode ? index + 3 : index + 2,
          errors: errors,
        };
      })
      .filter(Boolean);

    return (
      <>
        <div>
          The import process is finished. {successfulCreationOfData.length} out
          of {results.length} {isAssignmentMode ? 'assignment ' : 'customer '}
          records were created successfully. Please double-check the following
          columns and lines:
        </div>
        {arrayOfErrors.map((errors) => (
          <ul key={errors?.line}>
            <li>
              {errors?.errors.join(', ')} on {errors?.line} line.
            </li>
          </ul>
        ))}
        <div>
          It is not required to upload the full file again. You may fix the
          errors and leave only missing{' '}
          {isAssignmentMode ? 'assignments' : 'customers'} for the next attempt.
        </div>
      </>
    );
  };

  return (
    <BaseModal
      title="Import files"
      maxWidth={600}
      destroyOnClose
      open
      onCancel={handleCloseModal}
    >
      <div className="modal-scroll-wrapper">
        <div className={styles.title}>{getCorrectTitle()}</div>
        <div className={styles.message}>{getCorrectDescription()}</div>
      </div>
      <BaseModalFooter>
        <Button onClick={handleCloseModal}>Cancel</Button>

        <Button type="primary">
          <Link
            to={
              isAssignmentMode
                ? `/todo?section=${AdminRequestsSections.Assignments}`
                : `/todo?section=${AdminRequestsSections.Clients}`
            }
          >
            {isAssignmentMode ? 'Go to Assignment list' : 'Go to Customer list'}
          </Link>
        </Button>
      </BaseModalFooter>
    </BaseModal>
  );
}
